import React, {
  forwardRef,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { X, ChevronDown, Check } from 'lucide-react';
import InputError from './InputError';

interface Option {
  value: string;
  label: string;
}

interface LabeledMultiSelectProps {
  label: string;
  error?: string;
  options: Option[];
  placeholder?: string;
  value?: string[];
  onChange?: (value: string[]) => void;
  onBlur?: () => void;
}

const LabeledMultiSelect = forwardRef<
  HTMLFieldSetElement,
  LabeledMultiSelectProps
>(
  (
    {
      label,
      error,
      options,
      placeholder = 'Select options',
      value = [],
      onChange,
      onBlur,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef<HTMLDivElement>(null);

    const filteredOptions = options.filter(
      (option) =>
        option.label?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !value.includes(option.value)
    );

    const selectedOptions = options.filter((option) =>
      value.includes(option.value)
    );

    const handleSelect = useCallback(
      (optionValue: string) => {
        const newValue = [...value, optionValue];
        onChange?.(newValue);
        setSearchTerm('');
      },
      [value, onChange]
    );

    const handleRemove = useCallback(
      (optionValue: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const newValue = value.filter((val) => val !== optionValue);
        onChange?.(newValue);
      },
      [value, onChange]
    );

    const handleClickOutside = useCallback(
      (event: MouseEvent) => {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
          onBlur?.();
        }
      },
      [onBlur]
    );

    const handleEscapeKey = useCallback(
      (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsOpen(false);
          onBlur?.();
        }
      },
      [onBlur]
    );

    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }, [handleClickOutside, handleEscapeKey]);

    return (
      <fieldset className="form-control w-full" ref={ref}>
        <legend className="label">
          <span className="fieldset-legend text-primary font-semibold text-sm">
            {label}
          </span>
        </legend>

        <div className="relative" ref={wrapperRef}>
          <div
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setIsOpen(true)}
            onClick={() => setIsOpen(true)}
            className={`flex flex-wrap gap-2 p-2 min-h-[3.5rem] items-center bg-white rounded-lg znario:rounded-none border ${error ? 'border-error' : 'border-gray-300'} cursor-text`}
          >
            {value.length === 0 ? (
              <span className="text-gray-400 px-2">{placeholder}</span>
            ) : (
              selectedOptions.map((option) => (
                <div
                  key={option.value}
                  className="flex items-center gap-1 bg-gray-100 rounded-full px-3 py-1 text-sm"
                >
                  {option.label}
                  <button
                    type="button"
                    onClick={(e) => handleRemove(option.value, e)}
                    className="text-gray-500 hover:text-gray-700"
                    aria-label={`Remove ${option.label}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))
            )}
            <input
              type="text"
              className="flex-1 min-w-[100px] outline-none px-2"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setIsOpen(true)}
              placeholder={value.length === 0 ? '' : 'Type to search...'}
              aria-haspopup="listbox"
            />
            <ChevronDown
              className={`h-5 w-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>

          {isOpen && (
            <div
              className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg znario:rounded-none shadow-lg max-h-60 overflow-auto"
              role="listbox"
            >
              {filteredOptions.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-500">
                  No options found
                </div>
              ) : (
                <ul>
                  {filteredOptions.map((option) => (
                    <li
                      key={option.value}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between"
                      role="option"
                      aria-selected={value.includes(option.value)}
                      onClick={() => handleSelect(option.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSelect(option.value);
                        }
                      }}
                    >
                      {option.label}
                      {value.includes(option.value) && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <InputError error={error} />
      </fieldset>
    );
  }
);

export default LabeledMultiSelect;
